import { useEffect, useRef, useState } from "react";
import {
  useGetSenderDnsRecords,
  useRefreshSenderConfig,
  getGetSenderDnsRecordsQueryKey,
  getHealthCheckQueryKey,
  getGetDashboardSummaryQueryKey,
  type SenderDnsRecord,
  type SenderConfig,
  type HealthStatus,
  type DashboardSummary,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  CheckCircle2,
  Clock,
  Cloud,
  Copy,
  ExternalLink,
  HelpCircle,
  Loader2,
  RotateCw,
  ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";
import {
  apiErrorMessage,
  apiErrorRateLimit,
  apiErrorStatus,
} from "@/lib/apiError";

const CLOUDFLARE_DOCS_URL =
  "https://developers.cloudflare.com/dns/manage-dns-records/how-to/create-dns-records/";

async function copyToClipboard(value: string): Promise<boolean> {
  // Prefer the modern Clipboard API; fall back to a hidden textarea for
  // older browsers and for non-secure-context dev previews where
  // navigator.clipboard is gated off.
  if (
    typeof navigator !== "undefined" &&
    navigator.clipboard &&
    typeof navigator.clipboard.writeText === "function"
  ) {
    try {
      await navigator.clipboard.writeText(value);
      return true;
    } catch {
      // fall through to the legacy path
    }
  }
  if (typeof document === "undefined") return false;
  try {
    const ta = document.createElement("textarea");
    ta.value = value;
    ta.setAttribute("readonly", "");
    ta.style.position = "absolute";
    ta.style.left = "-9999px";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

interface CopyButtonProps {
  value: string;
  label: string;
  testId: string;
}

function CopyButton({ value, label, testId }: CopyButtonProps) {
  const [copied, setCopied] = useState(false);

  const onClick = async () => {
    const ok = await copyToClipboard(value);
    if (ok) {
      setCopied(true);
      toast.success(`${label} copied`);
      window.setTimeout(() => setCopied(false), 1500);
    } else {
      toast.error(`Couldn't copy ${label} — copy it manually.`);
    }
  };

  return (
    <Button
      type="button"
      size="sm"
      variant="outline"
      onClick={onClick}
      data-testid={testId}
      aria-label={`Copy ${label}`}
      className="h-7 px-2 gap-1.5 text-[11px]"
    >
      {copied ? (
        <CheckCircle2 className="h-3 w-3 text-emerald-600 dark:text-emerald-400" />
      ) : (
        <Copy className="h-3 w-3" />
      )}
      <span>{copied ? "Copied" : "Copy"}</span>
    </Button>
  );
}

function VerifiedBadge({ verified }: { verified: SenderDnsRecord["verified"] }) {
  if (verified === "verified") {
    return (
      <Badge
        variant="outline"
        className="text-emerald-700 border-emerald-500/30 bg-emerald-500/10 dark:text-emerald-400"
        data-testid="dns-record-verified"
      >
        <CheckCircle2 className="w-3 h-3 mr-1" />
        Verified
      </Badge>
    );
  }
  if (verified === "pending") {
    return (
      <Badge
        variant="outline"
        className="text-amber-700 border-amber-500/30 bg-amber-500/10 dark:text-amber-400"
        data-testid="dns-record-pending"
      >
        <Clock className="w-3 h-3 mr-1" />
        Not detected yet
      </Badge>
    );
  }
  return (
    <Badge
      variant="outline"
      className="text-muted-foreground border-border bg-muted/40"
      data-testid="dns-record-unknown"
    >
      <HelpCircle className="w-3 h-3 mr-1" />
      Recommended
    </Badge>
  );
}

function purposeLabel(purpose: SenderDnsRecord["purpose"]): string {
  switch (purpose) {
    case "SPF":
      return "SPF (Return-Path)";
    case "DKIM":
      return "DKIM";
    case "DMARC":
      return "DMARC";
    case "MX":
      return "MX (Return-Path)";
    default:
      return purpose;
  }
}

function makeRecordTestId(record: SenderDnsRecord): string {
  // Cloudflare-style key: "<purpose>-<type>-<sanitized name>" — unique per
  // record and stable enough for e2e tests to target.
  const safeName = record.name.replace(/[^a-z0-9]+/gi, "-");
  return `dns-record-${record.purpose.toLowerCase()}-${record.type.toLowerCase()}-${safeName}`;
}

export function DnsRecordsCard() {
  const dnsQuery = useGetSenderDnsRecords({
    query: {
      queryKey: getGetSenderDnsRecordsQueryKey(),
      // DNS records change rarely and the underlying Resend call is paid;
      // a 5-minute stale window mirrors the existing health-check cache.
      staleTime: 5 * 60 * 1000,
    },
  });

  // Show users when the per-record verification badges were last fetched
  // from Resend so they can tell whether the snapshot already reflects the
  // records they just pasted into Cloudflare. Updated in place by the
  // recheck button so the timestamp jumps to "just now" on success.
  const fetchedAt = dnsQuery.data?.fetchedAt ?? null;

  return (
    <Card data-testid="card-dns-records">
      <CardHeader>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <CardTitle className="text-lg flex items-center gap-2">
              <Cloud className="w-5 h-5 text-primary" /> DNS records (Cloudflare)
            </CardTitle>
            <CardDescription>
              The records below are what mail providers (Gmail, Outlook,
              Yahoo) check before delivering your email. Paste them into
              Cloudflare exactly as shown.
            </CardDescription>
          </div>
          <DnsRecheckButton
            fetchedAt={fetchedAt}
            onRefetch={() => dnsQuery.refetch()}
            disabled={dnsQuery.isLoading}
          />
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="rounded-md border border-border bg-muted/30 p-3 text-xs space-y-2">
          <div className="flex items-start gap-2">
            <ShieldCheck className="w-4 h-4 shrink-0 mt-0.5 text-primary" />
            <div className="space-y-1">
              <p className="font-medium text-foreground">
                Where these go in Cloudflare
              </p>
              <p className="text-muted-foreground">
                In Cloudflare, open <span className="font-mono">DNS → Records → Add record</span>.
                Pick the <span className="font-mono">Type</span> shown below,
                paste the <span className="font-mono">Name</span> and{" "}
                <span className="font-mono">Value</span>, leave{" "}
                <span className="font-mono">TTL</span> on{" "}
                <span className="font-mono">Auto</span>, and set{" "}
                <span className="font-mono">Proxy status</span> to{" "}
                <span className="font-mono">DNS only</span> (grey cloud).
                DNS changes can take a few minutes to propagate.
              </p>
              <a
                href={CLOUDFLARE_DOCS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-primary hover:underline"
                data-testid="dns-records-cloudflare-docs"
              >
                Cloudflare docs: How to create DNS records
                <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </div>
        </div>

        {dnsQuery.isLoading ? (
          <div
            className="flex justify-center py-8"
            data-testid="dns-records-loading"
          >
            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
          </div>
        ) : dnsQuery.isError ? (
          <div
            className="rounded-md border border-red-500/30 bg-red-500/5 p-4 text-sm text-red-700 dark:text-red-300"
            data-testid="dns-records-error"
          >
            Couldn't load DNS records. Refresh the page to try again.
          </div>
        ) : !dnsQuery.data?.configured ||
          (dnsQuery.data?.records?.length ?? 0) === 0 ? (
          <div
            className="rounded-md border border-dashed border-border p-6 text-sm text-muted-foreground space-y-2"
            data-testid="dns-records-empty"
          >
            <p>
              {dnsQuery.data?.reason ??
                "No sending domain is configured yet."}
            </p>
            <p className="text-xs">
              Once you add and verify a sending domain in the{" "}
              <a
                href="#sender-card"
                className="text-primary hover:underline"
                data-testid="dns-records-empty-sender-link"
              >
                Sender section above
              </a>{" "}
              (or in the{" "}
              <a
                href="https://resend.com/domains"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                Resend dashboard
              </a>
              ), the records you need to paste into Cloudflare will appear
              here.
            </p>
          </div>
        ) : (
          <DnsRecordsTable records={dnsQuery.data.records} />
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Mirror of the server-side `SENDER_RECHECK_LIMIT.windowMs` (see
 * `artifacts/api-server/src/lib/rateLimit.ts`). Re-checking DNS records
 * piggy-backs on the same Resend domains API as the sender recheck, so
 * the UI keeps the same per-user 10s cooldown to match.
 */
const DNS_RECHECK_COOLDOWN_MS = 10 * 1000;
const DNS_RECHECK_COOLDOWN_SECONDS = DNS_RECHECK_COOLDOWN_MS / 1000;

function secondsUntil(timestampMs: number, nowMs: number): number {
  return Math.max(0, Math.ceil((timestampMs - nowMs) / 1000));
}

/**
 * Format an ISO timestamp as a coarse "X ago" string for the
 * "Last checked" line. Coarse on purpose — DNS propagation takes
 * minutes, not seconds, so e.g. "12s ago" is more useful than
 * "12.3 seconds ago" and we don't want to re-render every tick.
 */
function formatRelative(iso: string, nowMs: number): string {
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return "just now";
  const deltaSec = Math.max(0, Math.floor((nowMs - ms) / 1000));
  if (deltaSec < 5) return "just now";
  if (deltaSec < 60) return `${deltaSec}s ago`;
  const mins = Math.floor(deltaSec / 60);
  if (mins < 60) return `${mins} minute${mins === 1 ? "" : "s"} ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs === 1 ? "" : "s"} ago`;
  const days = Math.floor(hrs / 24);
  return `${days} day${days === 1 ? "" : "s"} ago`;
}

interface DnsRecheckButtonProps {
  fetchedAt: string | null;
  onRefetch: () => Promise<unknown>;
  disabled?: boolean;
}

/**
 * "Re-check status" affordance for the DNS records card. Two jobs:
 *   1. Nudge Resend to re-classify the sender domain via the existing
 *      `/healthz/sender/refresh` endpoint (rate-limited per-user 1/10s).
 *   2. Refetch the DNS records snapshot so the per-record badges and
 *      "Last checked" timestamp update in place without a page reload.
 *
 * The cooldown UX mirrors `RecheckSenderButton` — same 10s window,
 * same countdown label, same 429 handling — so the two buttons feel
 * like one feature even though they live on separate cards.
 */
function DnsRecheckButton({
  fetchedAt,
  onRefetch,
  disabled,
}: DnsRecheckButtonProps) {
  const queryClient = useQueryClient();
  const [cooldown, setCooldown] = useState<{
    activeUntilMs: number;
  } | null>(null);
  const [now, setNow] = useState<number>(() => Date.now());
  const onRefetchRef = useRef(onRefetch);
  onRefetchRef.current = onRefetch;

  // Re-render once a second so the "Last checked" label and cooldown
  // countdown both stay current without a parent re-render. Using a
  // single timer for both purposes keeps this card dirt cheap.
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  // When a cooldown elapses, clear it so the button enables itself
  // without waiting for the user to interact again.
  useEffect(() => {
    if (cooldown == null) return;
    if (now >= cooldown.activeUntilMs) setCooldown(null);
  }, [cooldown, now]);

  const refreshMutation = useRefreshSenderConfig({
    mutation: {
      onSuccess: async (fresh: SenderConfig) => {
        // Patch the cached sender health/dashboard snapshots in place so
        // the rest of the page (Dashboard pill, Brand sender card)
        // immediately reflects the freshly-classified sender, mirroring
        // what `RecheckSenderButton` does on success.
        const healthKey = getHealthCheckQueryKey();
        const cachedHealth = queryClient.getQueryData<HealthStatus>(healthKey);
        if (cachedHealth) {
          queryClient.setQueryData<HealthStatus>(healthKey, {
            ...cachedHealth,
            senderConfig: fresh,
          });
        } else {
          queryClient.invalidateQueries({ queryKey: healthKey });
        }
        const summaryKey = getGetDashboardSummaryQueryKey();
        const cachedSummary =
          queryClient.getQueryData<DashboardSummary>(summaryKey);
        if (cachedSummary) {
          queryClient.setQueryData<DashboardSummary>(summaryKey, {
            ...cachedSummary,
            senderHealth: fresh,
          });
        }

        // Now pull a fresh DNS records snapshot so the per-record badges
        // and the "Last checked" timestamp update in place.
        try {
          await onRefetchRef.current();
        } catch {
          // Refetch errors surface through the existing dnsQuery.isError
          // path on the card, no need to double up with a toast.
        }

        // Server has just consumed this user's only token in the 10s
        // window — disable the button (with countdown) so a quick
        // double-click won't bounce off a 429.
        setCooldown({
          activeUntilMs: Date.now() + DNS_RECHECK_COOLDOWN_MS,
        });

        if (fresh.status === "verified") {
          toast.success("DNS records re-checked — sender is verified.");
        } else {
          toast.message(
            "DNS records re-checked. Verification may take a few minutes after Cloudflare publishes the records.",
          );
        }
      },
      onError: (err: unknown) => {
        if (apiErrorStatus(err) === 429) {
          const limit = apiErrorRateLimit(err);
          const resetMs = limit ? Date.parse(limit.resetAt) : NaN;
          if (Number.isFinite(resetMs) && resetMs > Date.now()) {
            setCooldown({
              activeUntilMs: resetMs,
            });
            toast.message(
              `Slow down — re-check is rate limited. Try again in ${secondsUntil(
                resetMs,
                Date.now(),
              )}s.`,
            );
            // 429 means the server rejected the nudge — no fresh data
            // was produced, so skip the DNS refetch.
            return;
          }
          toast.error(
            apiErrorMessage(
              err,
              "Re-check rate limit reached. Wait a few seconds before trying again.",
            ),
          );
          return;
        }

        toast.error(apiErrorMessage(err, "Couldn't re-check DNS records."));
        // Best-effort: even if nudging Resend failed (network blip,
        // 5xx, etc.), the DNS records endpoint is independent and may
        // still return a fresh snapshot. Pull it so the user at least
        // sees the latest verification state we know about.
        void onRefetchRef.current().catch(() => {
          // Refetch errors surface through dnsQuery.isError on the
          // card; no need to double up with a second toast.
        });
      },
    },
  });

  const isPending = refreshMutation.isPending;
  const secondsLeft =
    cooldown != null ? secondsUntil(cooldown.activeUntilMs, now) : 0;
  const isCountingDown = secondsLeft > 0;
  const isDisabled = Boolean(disabled) || isPending || isCountingDown;

  let label: string;
  if (isPending) {
    label = "Re-checking…";
  } else if (isCountingDown) {
    label = `Re-check in ${secondsLeft}s…`;
  } else {
    label = "Re-check status";
  }

  const button = (
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={() => refreshMutation.mutate()}
      disabled={isDisabled}
      data-testid="dns-records-recheck-button"
      aria-label="Re-check DNS verification status now"
      aria-live={isCountingDown ? "polite" : undefined}
      className="h-8"
    >
      {isPending ? (
        <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
      ) : (
        <RotateCw className="w-3.5 h-3.5 mr-1.5" />
      )}
      {label}
    </Button>
  );

  return (
    <div className="flex flex-col items-end gap-1 shrink-0">
      {isCountingDown ? (
        <Tooltip>
          <TooltipTrigger asChild>
            {/* Wrap the disabled button so the tooltip still receives
                pointer events (disabled buttons swallow them). */}
            <span tabIndex={0} className="inline-block">
              {button}
            </span>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs">
            Resend lets us re-check once every {DNS_RECHECK_COOLDOWN_SECONDS}{" "}
            seconds. Try again in {secondsLeft} second
            {secondsLeft === 1 ? "" : "s"}.
          </TooltipContent>
        </Tooltip>
      ) : (
        button
      )}
      <p
        className="text-[11px] text-muted-foreground"
        data-testid="dns-records-last-checked"
      >
        {fetchedAt
          ? `Last checked ${formatRelative(fetchedAt, now)}`
          : "Not yet checked"}
      </p>
    </div>
  );
}

interface DnsRecordsTableProps {
  records: SenderDnsRecord[];
}

function DnsRecordsTable({ records }: DnsRecordsTableProps) {
  return (
    <div
      className="overflow-hidden rounded-md border border-border"
      data-testid="dns-records-table"
    >
      {/* Desktop / wide layout: a true table with a header row. */}
      <table className="hidden md:table w-full text-xs">
        <thead className="bg-muted/40 text-muted-foreground">
          <tr className="text-left">
            <th className="px-3 py-2 font-medium">Purpose</th>
            <th className="px-3 py-2 font-medium">Type</th>
            <th className="px-3 py-2 font-medium">Name</th>
            <th className="px-3 py-2 font-medium">Value</th>
            <th className="px-3 py-2 font-medium">TTL</th>
            <th className="px-3 py-2 font-medium">Status</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {records.map((record, idx) => (
            <tr
              key={`${record.purpose}-${record.type}-${record.name}-${idx}`}
              className="align-top"
              data-testid={makeRecordTestId(record)}
            >
              <td className="px-3 py-3 font-medium text-foreground whitespace-nowrap">
                {purposeLabel(record.purpose)}
              </td>
              <td className="px-3 py-3 font-mono whitespace-nowrap">
                {record.type}
                {record.priority != null && (
                  <span className="text-muted-foreground">
                    {" "}
                    · prio {record.priority}
                  </span>
                )}
              </td>
              <td className="px-3 py-3 font-mono break-all">
                <div className="flex items-start gap-1.5">
                  <span className="flex-1 min-w-0 break-all">
                    {record.name}
                  </span>
                  <CopyButton
                    value={record.name}
                    label="name"
                    testId={`${makeRecordTestId(record)}-copy-name`}
                  />
                </div>
              </td>
              <td className="px-3 py-3 font-mono break-all">
                <div className="flex items-start gap-1.5">
                  <span className="flex-1 min-w-0 break-all">
                    {record.value}
                  </span>
                  <CopyButton
                    value={record.value}
                    label="value"
                    testId={`${makeRecordTestId(record)}-copy-value`}
                  />
                </div>
                <p className="mt-1.5 text-[11px] text-muted-foreground italic">
                  {record.note}
                </p>
              </td>
              <td className="px-3 py-3 font-mono whitespace-nowrap">
                {record.ttl}
              </td>
              <td className="px-3 py-3 whitespace-nowrap">
                <VerifiedBadge verified={record.verified} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Mobile / narrow layout: stacked cards with field labels so the
          row stays readable below ~md without the table going horizontal. */}
      <ul className="md:hidden divide-y divide-border">
        {records.map((record, idx) => (
          <li
            key={`mobile-${record.purpose}-${record.type}-${record.name}-${idx}`}
            className="p-3 space-y-2 text-xs"
            data-testid={`${makeRecordTestId(record)}-mobile`}
          >
            <div className="flex items-center justify-between gap-2">
              <span className="font-medium text-foreground">
                {purposeLabel(record.purpose)}
              </span>
              <VerifiedBadge verified={record.verified} />
            </div>
            <div className="grid grid-cols-[80px_1fr] gap-x-2 gap-y-1">
              <span className="text-muted-foreground">Type</span>
              <span className="font-mono">
                {record.type}
                {record.priority != null && (
                  <span className="text-muted-foreground">
                    {" "}
                    · prio {record.priority}
                  </span>
                )}
              </span>
              <span className="text-muted-foreground">TTL</span>
              <span className="font-mono">{record.ttl}</span>
            </div>
            <div className="space-y-1">
              <div className="flex items-center justify-between gap-2">
                <span className="text-muted-foreground">Name</span>
                <CopyButton
                  value={record.name}
                  label="name"
                  testId={`${makeRecordTestId(record)}-mobile-copy-name`}
                />
              </div>
              <div className="font-mono break-all rounded bg-muted/40 px-2 py-1.5">
                {record.name}
              </div>
            </div>
            <div className="space-y-1">
              <div className="flex items-center justify-between gap-2">
                <span className="text-muted-foreground">Value</span>
                <CopyButton
                  value={record.value}
                  label="value"
                  testId={`${makeRecordTestId(record)}-mobile-copy-value`}
                />
              </div>
              <div className="font-mono break-all rounded bg-muted/40 px-2 py-1.5">
                {record.value}
              </div>
            </div>
            <p className="text-[11px] text-muted-foreground italic">
              {record.note}
            </p>
          </li>
        ))}
      </ul>
    </div>
  );
}
